import { useEffect, useState } from "react";
import { formatEther } from "viem";
import type { MintFeeView, ProofEligibilityView, TierDisplayView } from "../../../state/ui-types";
import type { MintReviewView } from "../types";
import { InfoIcon } from "../../../components/Icons";
import { explainBlockingReason } from "../../../components/format";
import { Overlay } from "../../../components/overlays/Overlay";
import { responseSecondsRemaining } from "../../../api/response-time";
import { TierDisplayNotice } from "../../../components/TierDisplayNotice";
import { TierPortrait, tierName } from "../../../components/TierPortrait";
import { MintErrorNotice } from "./MintErrorNotice";

interface MintConfirmProps {
  open: boolean;
  tierUsd: number | null;
  fee: MintFeeView;
  review?: MintReviewView | null;
  tierDisplay?: TierDisplayView | undefined;
  proofEligibility?: ProofEligibilityView | undefined;
  demoMode: boolean;
  busy: boolean;
  preparing?: boolean;
  rewardRange?: string | null;
  transactionBlocked?: boolean;
  transactionMessage?: string | undefined;
  errorMessage?: string | null;
  errorCode?: string | null;
  onClose(): void;
  onConfirm(): void;
  onRefresh?(): void;
}

export function MintConfirmationDialog({ open, tierUsd, fee, review = null, tierDisplay, proofEligibility, demoMode, busy, preparing, rewardRange, transactionBlocked = false, transactionMessage, errorMessage, errorCode, onClose, onConfirm, onRefresh }: MintConfirmProps) {
  const tierFeeWei = tierUsd === null ? undefined : fee.tierMintFeesWei?.[String(tierUsd)];
  const estimatedFee = fee.tierMintFeesWei ? tierFeeWei === undefined ? null : formatEther(BigInt(tierFeeWei)) : fee.currentFeeEth;
  const [remaining, setRemaining] = useState(() => review ? responseSecondsRemaining(review, review.deadline) : 0);
  useEffect(() => {
    if (!open || !review) return;
    setRemaining(responseSecondsRemaining(review, review.deadline));
    const timer = setInterval(() => setRemaining(responseSecondsRemaining(review, review.deadline)), 1_000);
    return () => clearInterval(timer);
  }, [open, review]);
  const expired = review !== null && remaining === 0;
  const canPrepare = (fee.preparationAvailable ?? fee.mintEnabled) && (demoMode ||
    proofEligibility?.decision.state === "ALLOWED" && tierUsd !== null && proofEligibility.decision.usableTiers?.includes(tierUsd));
  const canAct = review && !expired ? fee.mintEnabled : canPrepare;
  const busyLabel = (preparing ?? (!review || expired)) ? "Checking mint conditions…" : "Processing mint…";
  return (
    <Overlay open={open} onClose={onClose} title="Review mint" eyebrow="FINAL CHECK">
      <section className="mint-fee-summary" aria-label="Mint fee">
        <h3>Mint fee</h3>
        <span>{review ? "Authorized mint fee" : fee.cacheStale ? "Last known mint fee (stale)" : "Estimated mint fee"}</span>
        <strong>{review ? formatEther(BigInt(review.feeWei)) : estimatedFee ?? "—"} {fee.nativeSymbol ?? "ETH"}</strong>
        <p>Network gas is additional</p>
      </section>
      <div className="mint-tier-identity">
        {tierUsd !== null ? <TierPortrait tier={tierUsd} /> : null}
        <div><strong>{tierUsd === null ? "—" : tierName(tierUsd)}</strong>
          <p>{tierUsd === null ? "—" : `$${tierUsd.toLocaleString("en-US")}`} eligibility tier · not a purchase price</p></div>
      </div>
      {(!review || expired) && tierDisplay ? <TierDisplayNotice display={tierDisplay} /> : null}
      <dl className="review-list mint-review-details">
        {review ? <>
          <div><dt>Exact value</dt><dd>{review.feeWei} wei</dd></div>
          <div><dt>Wallet</dt><dd>{review.account}</dd></div>
          <div><dt>Source token</dt><dd>{review.sourceToken}</dd></div>
          <div><dt>Execution chain</dt><dd>{review.chainId}</dd></div>
          <div><dt>Target contract</dt><dd>{review.collection}</dd></div>
          <div><dt>Quote expires</dt><dd>{new Date(review.deadline * 1_000).toLocaleString()} ({remaining}s remaining)</dd></div>
        </> : null}
        <div><dt>Reward range</dt><dd>{tierUsd === null ? "—" : rewardRange ?? "Policy unavailable"}</dd></div>
        <div><dt>Reward outcome</dt><dd>{fee.nativeSymbol === "HYPE" ? "Finalized in the mint transaction" : "Finalized after randomness"}</dd></div>
        <div><dt>Net buy consumed</dt><dd>No</dd></div>
      </dl>
      <div className="transaction-warning"><InfoIcon /><p>{review ? "This is the backend-authorized transaction. Gas is additional. We recheck eligibility, fee and expiry before opening your wallet; a changed quote requires a new review." : "Prepare a quote to review the exact transaction before opening your wallet. Preparation does not send a transaction or consume your proof."} A pending NFT cannot redeem until its reward is finalized.</p></div>
      {expired ? <p role="status">This quote expired. Prepare and review a new quote; nothing was sent.</p> : null}
      {fee.providerCheckMode === "ON_PREPARE" ? <p role="status">Quiver is checked when preparing a mint.</p> : null}
      {!canPrepare && (!review || expired) && fee.preparationReason ?
        <p role="status">{fee.preparationReason.split(", ").map(explainBlockingReason).join(" ")}</p> : null}
      {!demoMode && (!review || expired) && !canPrepare ? <p role="status">{proofEligibility?.error ??
        proofEligibility?.decision.message ?? "Refresh proof eligibility and choose an available tier."} {proofEligibility?.decision.reasons.join(", ")}</p> : null}
      {demoMode ? <div className="demo-send-warning">DEMO MODE · NO SENDABLE TRANSACTION</div> : null}
      {transactionMessage ? <p role="status">{transactionMessage}</p> : null}
      {errorMessage ? <MintErrorNotice message={errorMessage} code={errorCode ?? null} /> : null}
      <div className="modal-actions">
        <button type="button" className="secondary-action" disabled={busy} onClick={onClose}>Back</button>
        {!canPrepare && onRefresh ? <button type="button" className="secondary-action" disabled={busy} onClick={onRefresh}>Refresh mint conditions</button> : null}
        <button type="button" className="primary-action" disabled={busy || transactionBlocked || tierUsd === null || (!canAct && !demoMode)} onClick={onConfirm}>
          {busy ? busyLabel : transactionBlocked ? "Check transaction activity" : demoMode ? "Preview mint flow" : expired ? "Prepare a new quote" : review ? canAct ? "Confirm mint in wallet" : "Mint disabled" : "Check and prepare mint"}
        </button>
      </div>
    </Overlay>
  );
}
